import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Grid,
  Card,
  CardActionArea,
  Typography,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getTemplates } from '../../services/api';
import { BRAND, CATEGORY_COLORS } from '../../theme/tokens';
import TemplateThumbnail from './TemplateThumbnail';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'classic', label: 'Classic' },
  { id: 'modern', label: 'Modern' },
  { id: 'professional', label: 'Professional' },
  { id: 'creative', label: 'Creative' },
];

const LAYOUT_LABELS = {
  'single-column': 'Single column',
  'two-column': 'Sidebar',
  timeline: 'Timeline',
  split: 'Split',
  compact: 'Compact',
};

const TemplateSelector = ({ open, onClose, onSelectTemplate, selectedTemplateId }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await getTemplates();
        setTemplates(data);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      setLoading(true);
      fetchTemplates();
    }
  }, [open]);

  const filteredTemplates = templates.filter((template) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      template.name.toLowerCase().includes(q) ||
      template.description.toLowerCase().includes(q) ||
      (template.features || []).some((f) => f.toLowerCase().includes(q));
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const countFor = (cat) =>
    cat === 'all' ? templates.length : templates.filter((t) => t.category === cat).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          maxHeight: isMobile ? '100%' : '92vh',
          height: isMobile ? '100%' : 'auto',
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
          pb: 1.5,
          background: (t) =>
            t.palette.mode === 'dark'
              ? `linear-gradient(180deg, ${t.palette.background.default} 80%, transparent)`
              : `linear-gradient(180deg, ${t.palette.background.default} 70%, transparent)`,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <AutoAwesomeIcon sx={{ fontSize: 22, color: 'primary.main' }} />
              <Typography variant="h5" component="div" fontWeight={800} letterSpacing="-0.02em">
                Template gallery
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {templates.length
                ? `${templates.length} ATS-friendly layouts — pick one, then customize colors in preview`
                : 'Loading designs…'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.5, mr: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, style, or industry…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 0.5,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {CATEGORIES.map(({ id, label }) => {
            const active = categoryFilter === id;
            const catColor = id === 'all' ? null : CATEGORY_COLORS[id];
            return (
              <Chip
                key={id}
                label={`${label} · ${countFor(id)}`}
                clickable
                onClick={() => setCategoryFilter(id)}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  flexShrink: 0,
                  ...(active
                    ? {
                        bgcolor: catColor || 'primary.main',
                        color: '#fff',
                        borderColor: 'transparent',
                        '&:hover': { bgcolor: catColor || 'primary.dark' },
                      }
                    : {
                        borderColor: 'divider',
                        '&:hover': { borderColor: catColor || 'primary.light' },
                      }),
                }}
              />
            );
          })}
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, overflowY: 'auto', flex: 1 }}>
        {loading ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={10} gap={2}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              Loading templates…
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {filteredTemplates.map((template) => {
              const selected = selectedTemplateId === template.id;
              const catColor = CATEGORY_COLORS[template.category] || BRAND.primary;
              return (
                <Grid item xs={6} sm={4} md={3} key={template.id}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      position: 'relative',
                      borderWidth: selected ? 2 : 1,
                      borderStyle: 'solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                      '&:hover': {
                        boxShadow: (t) =>
                          t.palette.mode === 'dark'
                            ? '0 16px 40px rgba(0,0,0,0.45)'
                            : '0 16px 36px rgba(99, 102, 241, 0.16)',
                        transform: 'translateY(-4px)',
                        borderColor: selected ? 'primary.main' : 'primary.light',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => onSelectTemplate(template.id)}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      {selected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 2,
                            width: 26,
                            height: 26,
                            background: BRAND.gradient,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 10px rgba(99,102,241,0.45)',
                          }}
                        >
                          <CheckRoundedIcon sx={{ color: '#fff', fontSize: 16 }} />
                        </Box>
                      )}

                      <Box
                        sx={{
                          p: 1.5,
                          pb: 1,
                          bgcolor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(148,163,184,0.05)'
                              : 'rgba(15,23,42,0.025)',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <TemplateThumbnail template={template} />
                      </Box>

                      <Box sx={{ px: 1.5, pt: 1.25, pb: 1.5, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <Chip
                            label={template.category}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              textTransform: 'capitalize',
                              bgcolor: `${catColor}18`,
                              color: catColor,
                              border: `1px solid ${catColor}33`,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                            {LAYOUT_LABELS[template.layout] || template.layout}
                          </Typography>
                        </Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          color="text.primary"
                          sx={{ lineHeight: 1.3, mb: 0.35 }}
                        >
                          {template.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.45,
                          }}
                        >
                          {template.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25 }}>
                          <Box sx={{ display: 'flex', gap: 0.4 }}>
                            {[template.colors.primary, template.colors.accent, template.colors.sidebar].map(
                              (c, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: 11,
                                    height: 11,
                                    borderRadius: '50%',
                                    bgcolor: c,
                                    border: '1.5px solid rgba(15,23,42,0.1)',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                  }}
                                />
                              )
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {template.fonts.heading.split(',')[0].replace(/["']/g, '')}
                          </Typography>
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {!loading && filteredTemplates.length === 0 && (
          <Box textAlign="center" py={10}>
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              No templates found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try a different search or category filter
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default TemplateSelector;
